import { useEffect, useState } from 'preact/hooks';
import { listAllRecordsWithLinks, type RecordRow } from '../lib/api';
import { useRecordsVersion } from '../lib/recordEditor';
import { activeOnly } from '../lib/retired';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { humanizeValue } from '../lib/fieldDisplay';
import { Strip } from '../components/Strip';
import { AA, LINK } from '../lib/auditionSpread';
import {
  SUBMISSION_FILTERS,
  SUBMISSION_LINK,
  SUBMISSION_TYPE,
  matchesFilter,
  otherEndOf,
  sentDateWord,
  strField,
  type SubmissionFilter,
} from '../lib/submissions';
import { SubmissionStartSheet } from '../components/SubmissionStartSheet';
import './spread.css';

// B20a §2 — THE SUBMISSIONS LIST: opportunity → materials sent → invited,
// the household's own funnel BEFORE an audition exists. Same shape
// HouseholdAuditions took for A4 — a row OPENS THE SPREAD, never the
// schema-driven editor — and the same choice that screen made for its own
// stage strip: LOCAL component state, not the route, because
// `aa_submission`'s five states are this pack's alone (no legacy pack to
// collide with), and the "All/Drafts/Sent/Invited/Closed" vocabulary is
// fixed rather than derived — see lib/submissions.ts's own header.
//
// ⚑ B21 (2026-09-27) — THIS IS NO LONGER A SCREEN OF ITS OWN. Huan's ruling:
// submissions live ON the Auditions page ("don't want to push projects off
// the rails"), reached by that screen's leading "Submissions" strip option
// (and, in "All", a compact group above the audition list). So the read
// (`useSubmissionLines`) and the row shape (`SubmissionRow`) are exported
// for HouseholdAuditions.tsx to reuse verbatim — "the same rows" — and
// `SubmissionsListBody` is the whole strip+list+start-sheet body, exported
// so BOTH screens render the identical thing rather than two copies of it.
// `HouseholdSubmissions` itself is kept only as the thin "sp" + eyebrow
// wrapper around that body — nothing renders it as a screen any more (no
// route owns it), but its own test file still mounts it directly, and
// there is no reason to break that: the component still does exactly what
// it always did, standing alone.

export interface HouseholdSubmissionsProps {
  tenantId: string;
  onOpen: (submissionId: string) => void;
}

export interface SubmissionLine {
  submission: RecordRow;
  roleName: string | null;
  projectName: string | null;
  contactName: string | null;
}

/** The read, alone — no filter, no strip, no start sheet. Both
 * `SubmissionsListBody` below and HouseholdAuditions' own compact "All"
 * group call this directly rather than each holding a copy of the fetch +
 * sort + name-resolution logic. */
export function useSubmissionLines(tenantId: string): {
  lines: SubmissionLine[] | null;
  failure: ReadFailure | null;
  retry: () => void;
} {
  const [lines, setLines] = useState<SubmissionLine[] | null>(null);
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const recordsVersion = useRecordsVersion();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setFailure(null);
      try {
        const [subs, allRoles, allProjects, allPeople] = await Promise.all([
          listAllRecordsWithLinks(tenantId, SUBMISSION_TYPE, {
            linkTypes: [SUBMISSION_LINK.ROLE, SUBMISSION_LINK.PROJECT, SUBMISSION_LINK.CONTACT],
          }),
          listAllRecordsWithLinks(tenantId, AA.ROLE),
          listAllRecordsWithLinks(tenantId, AA.PROJECT),
          listAllRecordsWithLinks(tenantId, AA.PERSON),
        ]);
        if (cancelled) return;

        const roleName = new Map(activeOnly(allRoles.records).map((r) => [r.id, r.display_name]));
        const projectName = new Map(activeOnly(allProjects.records).map((r) => [r.id, r.display_name]));
        const personName = new Map(activeOnly(allPeople.records).map((r) => [r.id, r.display_name]));

        const next: SubmissionLine[] = activeOnly(subs.records).map((submission) => {
          const links = subs.linksByRecordId[submission.id] ?? [];
          const roleId = otherEndOf(links, submission.id, SUBMISSION_LINK.ROLE);
          const projectId = otherEndOf(links, submission.id, SUBMISSION_LINK.PROJECT);
          const contactId = otherEndOf(links, submission.id, SUBMISSION_LINK.CONTACT);
          return {
            submission,
            roleName: roleId ? roleName.get(roleId) ?? null : null,
            projectName: projectId ? projectName.get(projectId) ?? null : null,
            contactName: contactId ? personName.get(contactId) ?? null : null,
          };
        });
        // Newest first — same convention as the raw materials-sent moment
        // HouseholdAuditions reads: what went out most recently, then what
        // was created most recently for what has not gone out yet.
        next.sort((a, b) => {
          const ka = String(a.submission.fields.sent_at ?? a.submission.created_at);
          const kb = String(b.submission.fields.sent_at ?? b.submission.created_at);
          return ka < kb ? 1 : -1;
        });
        setLines(next);
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setLines(null);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, retryNonce, recordsVersion]);

  return { lines, failure, retry: () => setRetryNonce((n) => n + 1) };
}

/** ONE row — "opportunity (role · project), stage, sent date, contact" — the
 * single place that markup exists, so the compact group on Auditions' "All"
 * and the full list under its "Submissions" filter render byte-identical
 * rows rather than two hand-kept copies. */
export function SubmissionRow({ line, onOpen }: { line: SubmissionLine; onOpen: (id: string) => void }) {
  const opportunity = strField(line.submission, 'opportunity') ?? line.submission.display_name;
  const roleProject = [line.roleName, line.projectName].filter(Boolean).join(' · ') || null;
  const stageWord = line.submission.lifecycle_state ? humanizeValue(line.submission.lifecycle_state) : '';
  const sent = sentDateWord(strField(line.submission, 'sent_at'));
  return (
    <button type="button" class="sp__row" onClick={() => onOpen(line.submission.id)}>
      <span class="sp__row-top">
        <span class="sp__row-name">{opportunity}</span>
        <span class="sp__row-when">{stageWord}</span>
      </span>
      <span class="sp__row-sub">
        {[roleProject, sent, line.contactName]
          .filter(Boolean)
          .flatMap((part, i) => (i === 0 ? [part] : [' · ', part]))}
      </span>
    </button>
  );
}

/** The whole strip + list + "+ Submit for something" + start sheet — no
 * outer "sp"/eyebrow chrome, so a caller (HouseholdSubmissions below, or
 * HouseholdAuditions' own "Submissions" filter) supplies that itself and
 * this body drops in under it, unchanged either place. */
export function SubmissionsListBody({ tenantId, onOpen }: HouseholdSubmissionsProps) {
  const { lines, failure, retry } = useSubmissionLines(tenantId);
  const [filter, setFilter] = useState<SubmissionFilter>('all');
  const [starting, setStarting] = useState(false);

  if (failure) {
    return (
      <>
        <div class="sp__say">
          {failure.headline}
          <br />
          <span class="sp__say-soft">{failure.detail}</span>
        </div>
        {failure.retryable ? (
          <button type="button" class="sp__retry" onClick={retry}>
            Read the submissions again
          </button>
        ) : null}
      </>
    );
  }

  if (lines === null) {
    return <div class="sp__say">One moment…</div>;
  }

  const shown = lines.filter((l) => matchesFilter(l.submission.lifecycle_state, filter));

  return (
    <>
      <Strip items={SUBMISSION_FILTERS} activeId={filter} onSelect={(f) => setFilter(f)} />

      <div class="sp__acts">
        <button type="button" class="sp__act" data-testid="submissions-start" onClick={() => setStarting(true)}>
          + Submit for something
        </button>
      </div>

      {shown.length === 0 ? (
        <div class="sp__empty">{lines.length === 0 ? 'Nothing submitted yet.' : 'Nothing at this stage.'}</div>
      ) : (
        <div class="sp__list">
          {shown.map((line) => (
            <SubmissionRow key={line.submission.id} line={line} onOpen={onOpen} />
          ))}
        </div>
      )}

      {starting ? (
        <SubmissionStartSheet
          tenantId={tenantId}
          onClose={() => setStarting(false)}
          onCreated={(id) => {
            setStarting(false);
            onOpen(id);
          }}
        />
      ) : null}
    </>
  );
}

export function HouseholdSubmissions({ tenantId, onOpen }: HouseholdSubmissionsProps) {
  return (
    <div class="sp">
      <div class="sp__eyebrow">Submissions</div>
      <SubmissionsListBody tenantId={tenantId} onOpen={onOpen} />
    </div>
  );
}
